import {
  Grid,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  SxProps,
  useTheme,
} from '@mui/material';
import { memo, ReactNode, useContext } from 'react';
import { ROLES, Role, TOWPAY_ROLE_IDS } from 'types';
import WorkIcon from '@mui/icons-material/Work';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import MuiAvatar from '@mui/material/Avatar';
import { AppContext } from 'context/appContext';
import AuthService from 'utils/AuthService';

export interface RolesBoxProps {
  userRoles: Array<Role>;
  setUserRoles: (roles: Array<Role>) => void;
  readonly: boolean;
}

const RolesBoxComponent = ({
  userRoles,
  setUserRoles,
  readonly,
}: RolesBoxProps): ReactNode => {
  const { state } = useContext(AppContext);
  const { palette } = useTheme();

  const isRoleDisabled = (r: Role): boolean => {
    if (userRoles.some((userRole: Role) => userRole.id === 5) && r.id !== 5) {
      return true; // disallow all roles except property manager if property manager is already assigned
    }

    if (userRoles.some((userRole: Role) => userRole.id !== 5) && r.id === 5) {
      return true; // disallow property manager if another role is already assigned
    }

    if (TOWPAY_ROLE_IDS.includes(r.id)) {
      // Autura staff roles for the TowPay merchant management app, not tenant roles.
      // User 1 only, in any tenant. This has to be checked before the
      // allowUserCreationTowerRoles branch below, because that branch falls through to
      // `return false` (enabled) for any role it does not name, which would let a tenant
      // Admin grant themselves Towpay Admin. The JWT roles claim carries no tenant, so
      // merchant-management would then read that as "holds Towpay Admin" everywhere.
      return !AuthService.IsTheOne();
    }

    if (r.id === 1) {
      // Only The One can make SuperAdmin
      return !AuthService.IsTheOne();
    }

    if (AuthService.IsSuperAdmin()) {
      // allowUserCreationTowerRoles doesn't apply to SuperAdmin
      return false;
    }
    if (!state.tenant.data.allowUserCreationTowerRoles && r.id !== 5) {
      return true; // disallow all roles except property manager if allowUserCreationTowerRoles is false
    }
    if (r.id === 2) {
      // Only SuperAdmin or Admin can make Admin
      return !(AuthService.IsSuperAdmin() || AuthService.IsAdmin());
    }

    return false;
  };

  const isRoleVisible = (r: Role): boolean => {
    if (TOWPAY_ROLE_IDS.includes(r.id)) {
      // Super admins see the Towpay roles (disabled unless they are user 1), so they
      // can tell a user holds one. Everyone else never sees them.
      return AuthService.IsSuperAdmin() || AuthService.IsTheOne();
    }
    return !isRoleDisabled(r);
  };

  const isRoleSelected = (role: Role): boolean => {
    return userRoles.some(
      (r: Role) => r.tenantID === state.tenant.id && r.id === role.id,
    );
  };

  const handleToggle = (value: Role) => {
    if (readonly) return;
    if (isRoleSelected(value)) {
      setUserRoles([
        ...userRoles.filter(
          (r: Role) => !(r.id === value.id && r.tenantID === state.tenant.id),
        ),
      ]);
    } else {
      setUserRoles([
        ...userRoles,
        {
          ...value,
          permissions: value.permissions,
          tenantID: state.tenant.id,
          id: value.id,
        },
      ]);
    }
  };

  const getListItemStyles = (role: Role): SxProps => {
    if (!AuthService.IsTheOne() && role.id === 1) {
      return {
        display: 'none',
      };
    }
    return {
      border: '1px solid',
      borderRadius: 1,
      m: 0,
    };
  };

  return (
    <Grid container width={'100%'} spacing={1.5}>
      {ROLES.map(
        (role: Role) =>
          isRoleVisible(role) && (
            <Grid
              key={role.name}
              size={{
                xs: 12,
                sm: 6,
                lg: 3,
              }}
            >
              <ListItemButton
                key={role.id}
                onClick={() => handleToggle(role)}
                disabled={isRoleDisabled(role)}
                selected={isRoleSelected(role)}
                disableRipple={true}
                sx={getListItemStyles(role)}
              >
                <ListItemAvatar>
                  <MuiAvatar
                    sx={
                      isRoleSelected(role)
                        ? { backgroundColor: palette.primary.main }
                        : { backgroundColor: 'white' }
                    }
                  >
                    {isRoleSelected(role) ? (
                      <WorkIcon htmlColor={palette.primary.contrastText} />
                    ) : (
                      <WorkOutlineIcon htmlColor={'black'} />
                    )}
                  </MuiAvatar>
                </ListItemAvatar>
                <ListItemText
                  primary={role.name}
                  secondary={role.description}
                />
              </ListItemButton>
            </Grid>
          ),
      )}
    </Grid>
  );
};

export default memo(RolesBoxComponent);
